<template>
  <div class="multi-level-report-table">
    <el-table
      :data="data"
      v-loading="loading"
      border
      size="small"
      :max-height="maxHeight"
      :row-class-name="resolveRowClass"
      :header-cell-class-name="headerCellClass"
      style="width: 100%"
      @sort-change="handleSortChange"
    >
      <!-- ========== 左侧固定 11 列 ========== -->
      <el-table-column type="index" label="序号" width="58" fixed="left" align="center" />
      <el-table-column prop="businessType" label="业务类型" width="76" fixed="left" align="center">
        <template slot-scope="{ row }">{{ row.businessType || queryContext.businessTypeLabel || '-' }}</template>
      </el-table-column>
      <el-table-column label="年度" width="78" fixed="left" align="center">
        <template>{{ queryContext.year ? queryContext.year + '年' : '-' }}</template>
      </el-table-column>
      <el-table-column label="期次" width="68" fixed="left" align="center">
        <template>{{ periodLabel }}</template>
      </el-table-column>
      <el-table-column prop="daquName" label="战区" width="86" fixed="left" align="center" show-overflow-tooltip />
      <el-table-column prop="xiaoquName" label="单元" width="96" fixed="left" align="center" show-overflow-tooltip />
      <el-table-column prop="storeCode" label="门店编号" width="112" fixed="left" align="center" />
      <el-table-column prop="name" label="门店简称" width="160" fixed="left" show-overflow-tooltip />
      <el-table-column prop="avgScore" label="总体得分" width="96" fixed="left" align="center" sortable="custom" class-name="cell-num">
        <template slot-scope="{ row }">
          <span :class="{ 'cell-veto': row.isVeto }">{{ formatTotalScore(row) }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="daquRankInNational" label="全国排名" width="88" fixed="left" align="center" sortable="custom" class-name="cell-num" />
      <el-table-column prop="xiaoquRankInDaqu" label="战区排名" width="88" fixed="left" align="center" sortable="custom" class-name="cell-num" />

      <!-- ========== 一级指标分组（仅 Level=1 segment） ========== -->
      <el-table-column label="一级指标" align="center">
        <el-table-column
          v-for="seg in topSegments"
          :key="'seg_' + seg.segmentKey"
          :label="seg.letterCode || seg.segmentName"
          align="center"
          width="86"
          class-name="cell-num"
        >
          <template slot="header">
            <div class="header-stack header-segment">
              <div class="header-letter">{{ seg.letterCode }}</div>
              <div class="header-name" :title="seg.segmentName">{{ seg.segmentName }}</div>
            </div>
          </template>
          <template slot-scope="{ row }">
            <span v-if="metricType === 1">{{ formatScore(row.segmentScores && row.segmentScores[seg.segmentKey]) }}</span>
            <span v-else>{{ formatRate(row.segmentScoreRates && row.segmentScoreRates[seg.segmentKey]) }}</span>
          </template>
        </el-table-column>
      </el-table-column>

      <!-- ========== 二级指标 / 题项（按一级 segment 分组；空一级指标不渲染） ========== -->
      <el-table-column
        v-for="seg in topSegmentsWithItems"
        :key="'items_' + seg.segmentKey"
        :label="seg.segmentName"
        align="center"
      >
        <el-table-column
          v-for="item in itemsBySegment[seg.segmentKey] || []"
          :key="item.itemKey"
          align="center"
          width="84"
          :class-name="'cell-num' + (item.isBonus ? ' bonus-col' : '')"
        >
          <template slot="header">
            <div class="header-stack header-item">
              <div class="header-letter">{{ item.topSegmentLetter }}</div>
              <div class="header-parent" :title="item.topSegmentName">{{ item.topSegmentName }}</div>
              <div class="header-serial">
                {{ item.serialNo }}<span v-if="item.isBonus" class="bonus-tag">+</span>
              </div>
              <div class="header-label" :title="item.label">{{ item.label }}</div>
            </div>
          </template>
          <template slot-scope="{ row }">
            <ReportCell
              :status="row.itemStatuses && row.itemStatuses[item.itemKey]"
              :score="row.itemScores && row.itemScores[item.itemKey]"
              :rate="row.itemScoreRates && row.itemScoreRates[item.itemKey]"
              :options="optionRates && optionRates[item.itemKey]"
              :metric-type="metricType"
              :is-veto="row.isVeto"
              :is-bonus="item.isBonus"
            />
          </template>
        </el-table-column>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="multi-level-report-pagination">
      <div class="pagination-left">
        <span class="pagination-status">
          显示第 {{ rangeStart }}-{{ rangeEnd }} 条结果，共 {{ total }} 条
        </span>
        <el-pagination
          :page-size="pageSize"
          :page-sizes="pageSizes"
          :total="total"
          layout="sizes"
          @size-change="handleSizeChange"
        />
      </div>
      <el-pagination
        class="pagination-pager"
        :current-page="currentPage"
        :page-size="pageSize"
        :total="total"
        layout="prev, pager, next"
        background
        prev-text="上一页"
        next-text="下一页"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<script>
const ReportCell = {
  name: 'ReportCell',
  functional: true,
  props: {
    status: { type: String, default: null },
    score: { default: null },
    rate: { default: null },
    options: { type: Array, default: null },
    metricType: { type: Number, default: 1 },
    isVeto: { type: Boolean, default: false },
    isBonus: { type: Boolean, default: false }
  },
  render (h, { props }) {
    const { status, score, rate, options, metricType, isVeto, isBonus } = props
    if (status === 'NA') return h('span', { class: 'cell-na' }, '—')
    // 文档 48-§7.9：PENDING 必须显式标识为「待审」，不展示为 0 / NA
    if (status === 'PENDING') return h('span', { class: 'cell-pending', attrs: { title: '该题目所选审核级别下暂无审核结果' } }, '待审')
    if (isVeto) return h('span', { class: 'cell-veto' }, '0')

    if (metricType === 1) {
      const v = score == null ? '—' : Number(score)
      return h('span', { class: 'cell-num-value' }, v === '—' ? '—' : v.toString())
    }
    if (metricType === 2) {
      // 加分题在落实率 Tab 里直接显示分值（避免 >100% 无意义）
      if (isBonus) {
        const v = score == null ? '—' : Number(score)
        return h('span', { class: 'cell-num-value' }, v === '—' ? '—' : v.toString())
      }
      if (rate == null) return h('span', { class: 'cell-na' }, '—')
      return h('span', { class: 'cell-num-value' }, Number(rate).toFixed(1) + '%')
    }
    // metricType === 3: 占比
    if (!options || !options.length) return h('span', { class: 'cell-na' }, '—')
    const sorted = [...options].sort((a, b) => (b.rate || 0) - (a.rate || 0))
    const top = sorted[0]
    const popoverContent = sorted.map(o => {
      const pct = ((o.rate || 0) * 100).toFixed(1)
      return h('div', { class: 'opt-line', key: o.optionKey }, [
        h('span', { class: 'opt-label' }, o.optionLabel || o.optionKey),
        h('span', { class: 'opt-rate' }, `${pct}% (${o.count})`)
      ])
    })
    return h('el-popover', {
      props: { trigger: 'hover', placement: 'top', width: 220, popperClass: 'report-cell-popover' }
    }, [
      h('div', { class: 'opt-list' }, popoverContent),
      h('span', {
        slot: 'reference',
        class: 'cell-option-ref'
      }, top ? `${((top.rate || 0) * 100).toFixed(0)}%` : '—')
    ])
  }
}

export default {
  name: 'MultiLevelReportTable',
  components: { ReportCell },
  props: {
    data: { type: Array, default: () => [] },
    segmentColumns: { type: Array, default: () => [] },
    itemColumns: { type: Array, default: () => [] },
    optionRates: { type: Object, default: () => ({}) },
    metricType: { type: Number, default: 1 },
    scoringRuleCode: { type: String, default: 'simple' },
    loading: { type: Boolean, default: false },
    total: { type: Number, default: 0 },
    currentPage: { type: Number, default: 1 },
    pageSize: { type: Number, default: 10 },
    pageSizes: { type: Array, default: () => [10, 20, 50, 100] },
    reportType: { type: Number, default: 1 },
    queryContext: { type: Object, default: () => ({}) },
    maxHeight: { type: [Number, String], default: null },
    highlightStoreCode: { type: String, default: null }
  },
  computed: {
    periodLabel () {
      const ctx = this.queryContext || {}
      if (this.reportType === 1) return ctx.month ? ctx.month + '月' : '-'
      if (this.reportType === 2) return ctx.quarter ? 'Q' + ctx.quarter : '-'
      return '全年'
    },
    itemsBySegment () {
      const map = {}
      ;(this.itemColumns || []).forEach(item => {
        const key = item.topSegmentKey || ''
        if (!map[key]) map[key] = []
        map[key].push(item)
      })
      Object.keys(map).forEach(k => {
        map[k].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      })
      return map
    },
    /**
     * v1.5 修复 P1-01：表头分组只取 Level=1 的 segment，
     * 防止后端返回二级/三级 segment 时被误当作顶层重复渲染。
     * 接受两种调用：
     *   - 父组件已传入只含一级的列表（理想情形，直接用）
     *   - 父组件传入全量 segmentColumns（兜底，组件内部按 level 过滤）
     */
    topSegments () {
      const cols = this.segmentColumns || []
      const filtered = cols.filter(s => {
        const lvl = s.level ?? s.Level
        // 没有 level 字段时认为是一级（兼容历史响应）
        return lvl == null || lvl === 1
      })
      return filtered.slice().sort((a, b) =>
        (a.sortOrder ?? a.SortOrder ?? 0) - (b.sortOrder ?? b.SortOrder ?? 0))
    },
    /**
     * 二级指标分组层只渲染"该一级指标下确实有题项"的 segment，
     * 避免出现空白分组列。
     */
    topSegmentsWithItems () {
      const map = this.itemsBySegment
      return this.topSegments.filter(s => (map[s.segmentKey] || []).length > 0)
    },
    rangeStart () {
      if (!this.total) return 0
      return (this.currentPage - 1) * this.pageSize + 1
    },
    rangeEnd () {
      return Math.min(this.currentPage * this.pageSize, this.total)
    }
  },
  methods: {
    formatTotalScore (row) {
      if (row.isVeto) return '0 (否决)'
      const v = row.avgScore
      if (v == null) return '—'
      const num = Number(v)
      // percentage / bonusNormalized 是 0-100+ 的得分率，加 % 更直观
      if (this.scoringRuleCode === 'percentage' || this.scoringRuleCode === 'bonusNormalized') {
        return num.toFixed(1)
      }
      return num.toString()
    },
    formatScore (v) {
      if (v == null) return '—'
      return Number(v).toString()
    },
    formatRate (v) {
      if (v == null) return '—'
      return Number(v).toFixed(1) + '%'
    },
    resolveRowClass ({ row }) {
      const classes = []
      if (row && row.isVeto) classes.push('veto-row')
      if (this.highlightStoreCode && row && row.storeCode === this.highlightStoreCode) {
        classes.push('highlight-my-store')
      }
      return classes.join(' ')
    },
    headerCellClass ({ columnIndex, row, rowIndex }) {
      // row 0 是分组层；row 1 之后是叶子表头。
      // Element-UI 的多级表头里 rowIndex 从 0 起递增，每多一层 +1
      return rowIndex === 0 ? 'header-row-group' : 'header-row-leaf'
    },
    handleCurrentChange (page) {
      this.$emit('update:currentPage', page)
      this.$emit('page-change', page)
    },
    handleSizeChange (size) {
      this.$emit('update:pageSize', size)
      this.$emit('size-change', size)
    },
    handleSortChange ({ prop, order }) {
      this.$emit('sort-change', { prop, order })
    }
  }
}
</script>

<style lang="scss" scoped>
.multi-level-report-table {
  background: #fff;
  border: 1px solid #e6ebf2;
  border-radius: 8px;
  overflow: hidden;

  ::v-deep .el-table {
    color: #475569;
    font-size: 12px;
  }

  ::v-deep .el-table th.el-table__cell {
    background: #f8fafc;
    color: #334155;
    font-weight: 600;
    border-bottom: 1px solid #e6ebf2;
    padding: 4px 0;
    font-size: 12px;
    line-height: 1.3;
  }

  ::v-deep .el-table thead.is-group th.el-table__cell {
    background: #eef3fb;
    color: #1f2937;
    font-weight: 600;
  }

  ::v-deep .el-table th.el-table__cell.header-row-group {
    background: #eef3fb;
  }

  ::v-deep .el-table th.el-table__cell.header-row-leaf {
    background: #f8fafc;
  }

  ::v-deep .el-table td.el-table__cell {
    padding: 0 6px;
    height: 38px;
    color: #334155;
    border-bottom: 1px solid #eef2f6;
    border-right: 0;
  }

  // v1.5 修复 P2-04：减少表格主体竖线密度，仅保留表头分隔，主体只用横向分隔 + hover 高亮
  ::v-deep .el-table--border th.el-table__cell {
    border-right: 1px solid #eef2f6;
  }
  ::v-deep .el-table--border td.el-table__cell {
    border-right: none;
  }
  ::v-deep .el-table--border td.el-table__cell:last-child {
    border-right: 1px solid transparent;
  }
  ::v-deep .el-table--enable-row-hover .el-table__body tr:hover > td.el-table__cell {
    background-color: #f7faff !important;
  }

  ::v-deep .el-table .cell-num {
    font-variant-numeric: tabular-nums;
    text-align: center;
  }

  ::v-deep .el-table .cell-num .cell {
    text-align: center;
  }

  // 固定列阴影
  ::v-deep .el-table__fixed::before {
    display: none;
  }
  ::v-deep .el-table__fixed {
    box-shadow: 6px 0 10px rgba(15, 23, 42, .06);
  }

  // v1.5 修复 P2-04：加分题不再用整列大面积色块，仅保留 header 上的小标签 + 数字后微弱描边
  ::v-deep .el-table .bonus-col .cell-num-value {
    color: #b45309;
  }

  // 否决行
  ::v-deep .veto-row td.el-table__cell {
    background: #fff1f2 !important;
  }

  // 高亮本店行（bzmj 用）
  ::v-deep .highlight-my-store td.el-table__cell {
    background: #ecfdf5 !important;
  }

  .header-stack {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    line-height: 1.3;
    padding: 2px 0;
  }
  .header-letter {
    color: #6f86bd;
    font-weight: 700;
    font-size: 12px;
  }
  .header-name,
  .header-parent {
    color: #1f2937;
    font-size: 12px;
    margin-top: 2px;
    max-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .header-serial {
    color: #475569;
    font-size: 12px;
    font-weight: 600;
    margin-top: 2px;
  }
  .bonus-tag {
    color: #f59e0b;
    margin-left: 2px;
  }
  .header-label {
    color: #64748b;
    font-size: 11px;
    margin-top: 2px;
    max-width: 76px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  ::v-deep .cell-na {
    color: #94a3b8;
  }
  ::v-deep .cell-pending {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 3px;
    background: #fff7ed;
    color: #b45309;
    font-size: 11px;
    font-weight: 500;
    line-height: 1.4;
  }
  ::v-deep .cell-veto {
    color: #dc2626;
    font-weight: 600;
  }
  ::v-deep .cell-num-value {
    color: #1f2937;
    font-variant-numeric: tabular-nums;
  }
  ::v-deep .cell-option-ref {
    color: #3f65d8;
    cursor: help;
    font-variant-numeric: tabular-nums;
  }

  .multi-level-report-pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    height: auto;
    min-height: 48px;
    padding: 8px 12px;
    border-top: 1px solid #eef2f6;
    background: #fff;

    .pagination-left {
      display: flex;
      align-items: center;
      gap: 12px;
    }
    .pagination-status {
      color: #64748b;
      font-size: 12px;
    }

    ::v-deep .el-pagination {
      padding: 0;
      font-weight: normal;
    }
    ::v-deep .el-pagination.is-background .el-pager li:not(.disabled).active {
      background-color: #3f65d8;
      color: #fff;
    }
    ::v-deep .el-pagination.is-background .el-pager li:not(.disabled):hover {
      color: #3f65d8;
    }
    ::v-deep .el-pagination.is-background .el-pager li:not(.disabled).active:hover {
      color: #fff;
    }
    ::v-deep .pagination-pager .btn-prev,
    ::v-deep .pagination-pager .btn-next {
      padding: 0 12px !important;
      min-width: 64px;
      background: #fff !important;
      border: 1px solid #e6ebf2;
      color: #475569;
    }
    ::v-deep .pagination-pager .btn-prev:hover:not(:disabled),
    ::v-deep .pagination-pager .btn-next:hover:not(:disabled) {
      color: #3f65d8;
      border-color: #c3cfe5;
    }
    ::v-deep .pagination-pager .btn-prev:disabled,
    ::v-deep .pagination-pager .btn-next:disabled {
      color: #c0c4cc;
      background: #fff !important;
    }
  }
}
</style>

<style lang="scss">
// el-popover 内部样式（不能 scoped）
.report-cell-popover {
  .opt-list {
    .opt-line {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      padding: 4px 0;
      border-bottom: 1px solid #f1f5f9;
      font-size: 12px;
      &:last-child { border-bottom: 0; }
      .opt-label { color: #475569; }
      .opt-rate {
        color: #1f2937;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
      }
    }
  }
}
</style>
